import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Plus, Pencil, Trash2, CheckCircle2, RotateCcw, AlertTriangle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = [
  { id: 'kira', label: 'Kira' },
  { id: 'maas', label: 'Maaş' },
  { id: 'faturalar', label: 'Fatura' },
  { id: 'vergi', label: 'Vergi' },
  { id: 'aidat', label: 'Aidat' },
  { id: 'diger', label: 'Diğer' },
];
const catLabel = (id) => (CATEGORIES.find(c => c.id === id) || {}).label || id;
const formatTL = (n) => `₺${(parseFloat(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const emptyForm = { name: '', amount: '', due_day: 1, category: 'kira', notes: '' };

const StatusBadge = ({ item }) => {
  if (item.status === 'paid') return <Badge className="bg-emerald-100 text-emerald-700 border-0 gap-1" data-testid={`fe-status-${item.id}`}><CheckCircle2 className="w-3 h-3" /> Ödendi</Badge>;
  if (item.status === 'overdue') return <Badge className="bg-rose-100 text-rose-700 border-0 gap-1" data-testid={`fe-status-${item.id}`}><AlertTriangle className="w-3 h-3" /> Gecikti ({item.days_overdue} gün)</Badge>;
  if (item.status === 'upcoming') return <Badge className="bg-amber-100 text-amber-700 border-0 gap-1" data-testid={`fe-status-${item.id}`}><Clock className="w-3 h-3" /> Yaklaşıyor ({item.days_left} gün)</Badge>;
  return <Badge className="bg-slate-100 text-slate-600 border-0" data-testid={`fe-status-${item.id}`}>Bekliyor</Badge>;
};

const FixedExpensesTab = ({ period, onPaidChange }) => {
  const [data, setData] = useState({ items: [], total_amount: 0, paid_total: 0, unpaid_total: 0, overdue_count: 0 });
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/fixed-expenses`, { params: { period }, headers: authHeader() });
      setData(res.data || { items: [] });
    } catch (e) {
      setData({ items: [], total_amount: 0, paid_total: 0, unpaid_total: 0, overdue_count: 0 });
    } finally { setLoading(false); }
  }, [period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAdd = () => { setEditing(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = (it) => { setEditing(it); setForm({ name: it.name, amount: it.amount, due_day: it.due_day, category: it.category, notes: it.notes || '' }); setShowModal(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Gider adı zorunlu'); return; }
    setSaving(true);
    try {
      const body = { name: form.name.trim(), amount: Number(form.amount) || 0, due_day: Number(form.due_day) || 1, category: form.category, notes: form.notes };
      if (editing) await axios.put(`${API}/fixed-expenses/${editing.id}`, body, { headers: authHeader() });
      else await axios.post(`${API}/fixed-expenses`, body, { headers: authHeader() });
      toast.success(editing ? 'Sabit gider güncellendi' : 'Sabit gider eklendi');
      setShowModal(false);
      await fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Kaydedilemedi'); }
    finally { setSaving(false); }
  };

  const remove = async (it) => {
    if (!window.confirm(`"${it.name}" sabit giderini silmek istiyor musunuz?`)) return;
    try {
      await axios.delete(`${API}/fixed-expenses/${it.id}`, { headers: authHeader() });
      toast.success('Silindi');
      await fetchData();
    } catch (e) { toast.error('Silinemedi'); }
  };

  const togglePay = async (it) => {
    try {
      if (it.status === 'paid') {
        await axios.delete(`${API}/fixed-expenses/${it.id}/pay`, { params: { period }, headers: authHeader() });
        toast.success('Ödeme geri alındı');
      } else {
        await axios.post(`${API}/fixed-expenses/${it.id}/pay`, { period }, { headers: authHeader() });
        toast.success('Ödendi olarak işaretlendi — Kasa\'ya gider işlendi');
      }
      await fetchData();
      if (onPaidChange) onPaidChange();
    } catch (e) { toast.error(e?.response?.data?.detail || 'İşlem başarısız'); }
  };

  return (
    <div className="space-y-3" data-testid="fixed-expenses-tab">
      {/* Özet */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card><CardContent className="p-3"><p className="text-[10px] text-slate-500 uppercase tracking-wide">Toplam Sabit Gider</p><p className="text-lg font-bold text-slate-800">{formatTL(data.total_amount)}</p></CardContent></Card>
        <Card><CardContent className="p-3"><p className="text-[10px] text-slate-500 uppercase tracking-wide">Ödenen</p><p className="text-lg font-bold text-emerald-700">{formatTL(data.paid_total)}</p></CardContent></Card>
        <Card><CardContent className="p-3"><p className="text-[10px] text-slate-500 uppercase tracking-wide">Kalan</p><p className="text-lg font-bold text-amber-700">{formatTL(data.unpaid_total)}</p></CardContent></Card>
        <Card className={data.overdue_count > 0 ? 'border-rose-300' : ''}><CardContent className="p-3"><p className="text-[10px] text-slate-500 uppercase tracking-wide">Geciken</p><p className={`text-lg font-bold ${data.overdue_count > 0 ? 'text-rose-600' : 'text-slate-800'}`}>{data.overdue_count} kalem</p></CardContent></Card>
      </div>

      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">Seçili ay: <strong>{period}</strong></p>
        <Button size="sm" onClick={openAdd} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="add-fixed-expense-btn"><Plus className="w-4 h-4 mr-1" /> Sabit Gider Ekle</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <p className="text-center text-slate-400 py-6 text-sm">Yükleniyor...</p>
          ) : data.items.length === 0 ? (
            <p className="text-center text-slate-400 py-8 text-sm">Henüz sabit gider tanımlanmadı. "Sabit Gider Ekle" ile başlayın.</p>
          ) : (
            <div className="divide-y">
              {data.items.map(it => (
                <div key={it.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50" data-testid={`fixed-expense-row-${it.id}`}>
                  <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600 shrink-0">{it.due_day}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-800 truncate">{it.name}</span>
                      <Badge variant="secondary" className="text-[10px]">{catLabel(it.category)}</Badge>
                      <StatusBadge item={it} />
                    </div>
                    <p className="text-[11px] text-slate-400">Her ayın {it.due_day}'i • Vade: {it.due_date}{it.paid_date ? ` • Ödendi: ${it.paid_date}` : ''}{it.notes ? ` • ${it.notes}` : ''}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-bold text-slate-800">{formatTL(it.amount)}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" variant={it.status === 'paid' ? 'outline' : 'default'} onClick={() => togglePay(it)}
                      className={it.status === 'paid' ? 'h-8 text-xs border-slate-300' : 'h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white'}
                      data-testid={`fe-pay-${it.id}`}>
                      {it.status === 'paid' ? (<><RotateCcw className="w-3.5 h-3.5 mr-1" /> Geri Al</>) : (<><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Ödendi</>)}
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(it)} data-testid={`fe-edit-${it.id}`}><Pencil className="w-4 h-4 text-slate-500" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => remove(it)} data-testid={`fe-delete-${it.id}`}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md" data-testid="fixed-expense-modal">
          <DialogHeader>
            <DialogTitle>{editing ? 'Sabit Gider Düzenle' : 'Yeni Sabit Gider'}</DialogTitle>
            <DialogDescription>Aylık tekrarlayan giderinizi tanımlayın. Ödediğinizde Kasa'ya gider olarak işlenir.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Gider Adı *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ör: Ofis Kirası" data-testid="fe-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Tutar (₺) *</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0" data-testid="fe-amount-input" />
              </div>
              <div>
                <Label className="text-xs">Vade Günü (1-31) *</Label>
                <Input type="number" min="1" max="31" value={form.due_day} onChange={(e) => setForm({ ...form, due_day: e.target.value })} data-testid="fe-dueday-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Kategori</Label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full h-10 px-3 border border-slate-200 rounded-md text-sm bg-white" data-testid="fe-category-select">
                {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Not</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Opsiyonel" data-testid="fe-notes-input" />
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" onClick={() => setShowModal(false)} className="flex-1" disabled={saving}>İptal</Button>
              <Button onClick={save} disabled={saving} className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="fe-save-btn">{saving ? 'Kaydediliyor...' : 'Kaydet'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FixedExpensesTab;
